import { useEffect, useState, MouseEvent } from 'react'
import { useRouter } from 'next/router'
import { FlexboxGrid, Loader, Placeholder } from 'rsuite'
import useTranslation from 'next-translate/useTranslation'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

import ProductCard from './ProductCard'
import BannerCard from './Banner'
import banners from './banners'
import { Banner } from '../../../utils/types/misc'
import { Product } from '../../../utils/types/Product'
import { QueryParam, ShortcutUseCaseData, ShortcutUseCaseLablesWithIds } from '../../../utils/constants'
import useIsOnMobile from '../../../services/useIsOnMobile'
import ProductCardMobile from './ProductCardMobile'
import useCategoryDataStructure from '../../hooks/useCategoryDataStructure'
import { getCountryAndLocaleStrings } from '../../../utils/locales'
import searchResultClickEvent from '../../../utils/analytics/searchResultClickEvent'
import useSearchId from '../../hooks/useSearchId'

import styles from '../../../styles/ProductsList.module.less'

interface ProductListProps {
  products: Product[]
  isLoading: boolean
  total?: number
}

const LOADING_TEXT_SWITCH_INTERVAL = 1500

const isBanner = (card: Product | Banner): card is Banner => {
  const featureCard = card as Banner
  const allBannerTypes = banners.map((banner) => banner.type)

  return allBannerTypes.includes(featureCard.type)
}

const ProductsList: React.FC<ProductListProps> = (props) => {
  const { products, isLoading, total } = props

  const { query, pathname, locale: localeAndCountryString } = useRouter()
  const { locale, country } = getCountryAndLocaleStrings(localeAndCountryString)
  const { t } = useTranslation('products')
  const isOnMobile = useIsOnMobile()
  const { searchId } = useSearchId()

  // Check if we're on a supplier page
  const isSupplierPage = pathname.includes('[supplierCode]')

  const searchQuery = query[QueryParam.search]
  const useCaseQuery = query['f_use-case']

  const useCaseFeatureFlag = useFeatureIsOn('product-catalog-shortcut-use-case-filter-banner-card')
  const hideMarketingCards = useFeatureIsOn('product-feed-hide-marketing-cards')
  const isBorderlessProductCard = useFeatureIsOn('product-catalog-card-borderless-variant')

  const loadingTextList = [
    t('common:Loading standards'),
    t('common:Searching labels'),
    t('common:Fetching filters'),
    t('common:Categorizing products'),
    t('common:Verifying use-cases'),
  ]

  const [currentLoadingTextIndex, setCurrentLoadingTextIndex] = useState(0)

  const { parentNodes, selectedCategory } = useCategoryDataStructure()

  // Set a dynamic loading text index that will change every 1.5 seconds
  useEffect(() => {
    let intervalId: NodeJS.Timeout
    if (isLoading) {
      setCurrentLoadingTextIndex(0)
      intervalId = setInterval(() => {
        setCurrentLoadingTextIndex((prevIndex) => {
          if (prevIndex >= loadingTextList.length - 1) return 0
          return prevIndex + 1
        })
      }, LOADING_TEXT_SWITCH_INTERVAL)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [isLoading])

  const useCaseQueryIds = useCaseQuery ? useCaseQuery.toString().split('-') : []

  const allowedShortcutCategories = Object.keys(ShortcutUseCaseData)

  const bannersToShow = banners.filter((banner) => (
    (banner.minimumCards <= products.length)))
    .filter((currentBanner) => {
      const device = isOnMobile ? 'mobile' : 'desktop'
      if (currentBanner.hideOnDevice === device) {
        return false
      }

      // Skip ProductCategoryCarousel banners on supplier pages
      if (isSupplierPage && currentBanner.id === 'productCategoryCarousel') {
        return false
      }

      let ignorableBannerTitle = 'products:Secure payments'

      const doesIdExist = useCaseQueryIds
        .some((id) => Object.keys(ShortcutUseCaseLablesWithIds).includes(id))

      // Don't show shorcut banner if feature flag is false
      // or filter is already added
      // or CAT is not level 2
      // or CAT should not be allowed to have shorcuts
      if (!useCaseFeatureFlag
      || doesIdExist
      || parentNodes.length !== 2
      || isOnMobile
      || !allowedShortcutCategories.includes(selectedCategory)) {
        ignorableBannerTitle = 'Find products for your use case'
      }

      return currentBanner.title !== ignorableBannerTitle
    })
    .map((banner) => {
      let imageUrl = banner.image
      // Workaround to show correct banner images for all languages
      if (banner.image?.startsWith('d24-product-feed-sourcing')) {
        imageUrl = `d24-product-feed-sourcing-${locale}.webp`
      }
      if (banner.image?.startsWith('d24-product-feed-ordering')) {
        imageUrl = `d24-product-feed-ordering-${locale}.webp`
      }
      if (banner.image?.startsWith('d24-request-free-samples')) {
        imageUrl = `d24-request-free-samples-${locale}.webp`
      }

      return { ...banner, image: imageUrl }
    })

  const cards: (Product | Banner)[] = [...bannersToShow, ...products]

  // Analytics event for search result clicks
  const triggerSearchResultClickEvent = (
    event: MouseEvent<HTMLDivElement>,
    productSku: string,
    supplierName: string,
  ) => {
    if (searchQuery) {
      searchResultClickEvent({
        searchTerm: searchQuery?.toString() || '',
        locale,
        country,
        resultQuantity: total || 0,
        productSku,
        supplier: supplierName,
        context: 'search_results_page',
        searchDomain: 'product',
        searchId: searchId || 'direct',
        event,
      })
    }
  }

  return (
    <>
      {isLoading && (
        <FlexboxGrid
          justify="center"
          className={styles.loader}
        >
          <Loader
            content={loadingTextList[currentLoadingTextIndex]}
            data-testid="products-list-loader"
            className="margin-bottom-spacer-quadruple margin-top-spacer-quadruple"
          />
        </FlexboxGrid>
      )}
      <div
        className={`${styles['product-grid']} ${isLoading ? styles.opacity : ''}`}
        id="product-grid"
        data-testid="products-list"
      >
        {
          cards
            .map((card) => {
              if (isBanner(card)) {
                // Hide marketing cards if feature flag is on
                if (hideMarketingCards && card.classification === 'marketing-card') {
                  return null
                }

                return (
                  <BannerCard
                    banner={card}
                    key={`banner-${card.title}-${card.startRow}-${card.startColumn}-${card.spanY}-${card.spanX}`}
                    products={products}
                  />
                )
              }

              // Analytics tracking
              const handleAnalyticsClick = (
                event: MouseEvent<HTMLDivElement>,
              ) => {
                triggerSearchResultClickEvent(
                  event,
                  card['product.code'],
                  card.supplier[0]['supplier.label'] || '',
                )
              }

              if (isOnMobile) {
                return (
                  <ProductCardMobile
                    product={card}
                    key={card.id}
                    onClick={handleAnalyticsClick}
                  />
                )
              }

              if (isBorderlessProductCard) {
                return (
                  <ProductCardMobile
                    key={card.id}
                    product={card}
                    showMinimumOrderValue
                    onClick={handleAnalyticsClick}
                  />
                )
              }

              return (
                <ProductCard
                  product={card}
                  key={card.id}
                  onAnalyticsClick={handleAnalyticsClick}
                />
              )
            })
        }
      </div>

      {products.length === 0 && (
        <FlexboxGrid justify="center">
          <div
            data-testid="skeleton-cards-container"
            className={`${styles['product-grid']} margin-top-spacer-quadruple`}
          >
            {Array.from({ length: 8 }, (_, i) => (
              <Placeholder.Graph
                active
                key={i}
                className={styles['skeleton-loading-card']}
              />
            ))}
          </div>
        </FlexboxGrid>
      )}
    </>
  )
}
export default ProductsList
